import { destroyPriceRangeFilter, initPriceRangeFilter } from "../../Modules/Filters/PriceRange";
import LoaderOverlay from "../../Modules/LoaderOverlay";
import ModelOptions from "../../Modules/Filters/ModelOptions";
import { Dropdown } from "../Form/Dropdown";

const SECTION_MODEL = "hero-filter-model";
const SECTION_MODELOPTIONS = "product-collection-section-model-filter-options";
const SECTION_FRAME_TYPE = "hero-filter-frame_type";

const SECTIONS: { [key: string]: string } = {
    vendor: "hero-filter-vendor",
    price: "hero-filter-price_range",
    button: "hero-filter-button"
};

const SELECTORS = {
    price_lte: '[name="filter.v.price.lte"]',
    price_gte: '[name="filter.v.price.gte"]',
    categories: 'input[name="hero-category"]',
    vendor: 'x-dropdown[name="filter.p.vendor"]',
    model: 'x-dropdown[name="filter.p.m.product.model"]',
    frame_type: 'x-dropdown[name="filter.p.m.highlights.rahmenform"]'
};

class HeroFilter {
    form: HTMLFormElement;
    selections: HTMLElement;
    categories: HTMLElement;
    buttonBox: HTMLElement;
    parser: DOMParser;
    changeHandler: () => void;
    selects: { [key: string]: Dropdown };
    loadOverlay: LoaderOverlay;
    modelOptions?: ModelOptions;
    private currentModel: string;

    private buffer: {
        cache: { [key: string]: Promise<JSON> };
    } = { cache: {} };

    constructor() {
        this.changeHandler = this.render.bind(this);
        this.parser = new DOMParser();
        this.initElements().initSections().initForm().initCategories();
        try {
            this.render().then(/* ignore */);
        } catch (error) {}
    }

    /**
     * initialize sections according to available dropdowns
     */
    initSections() {
        if (this.form.querySelector(SELECTORS.model)) {
            this.modelOptions = new ModelOptions();
            SECTIONS.model = SECTION_MODEL;
            SECTIONS.modelOptions = SECTION_MODELOPTIONS;
        }
        if (this.form.querySelector(SELECTORS.frame_type)) {
            SECTIONS.frame_type = SECTION_FRAME_TYPE;
        }
        return this;
    }

    /**
     * initialize elements
     */
    initElements() {
        this.form = document.querySelector(".cms-hero-filter > form");
        this.categories = this.form.querySelector(":scope > section.categories");
        this.selections = this.form.querySelector(":scope > section.selections");
        this.buttonBox = this.form.querySelector(":scope > .button-box");
        this.selects = {
            "filter.v.price.gte": this.selections.querySelector<Dropdown>(SELECTORS.price_gte),
            "filter.v.price.lte": this.selections.querySelector<Dropdown>(SELECTORS.price_lte)
        };
        this.categories.querySelector<HTMLElement>(":scope > .std-scroller > .scroller").scrollTo(0, 0);
        this.loadOverlay = new LoaderOverlay(this.form);
        return this;
    }

    /**
     * initialize form
     */
    initForm() {
        this.form.addEventListener("submit", () => (this.form.action = this.selectedCollectionURL));
        return this;
    }

    /**
     * initialize categories
     */
    initCategories() {
        (this.form.querySelectorAll(SELECTORS.categories) as NodeListOf<HTMLInputElement>).forEach(
            (trigger: HTMLInputElement) => {
                trigger.addEventListener("click", this.changeHandler);
            }
        );
        return this;
    }

    /**
     * load data and render new elements
     */
    async render(e?: Event) {
        this.disabled = true;
        setTimeout(async () => {
            try {
                this.loadOverlay.render();
                let filterParams = this.filterParams;
                let modelOptions: HTMLOptionElement[] = [];
                const target = e?.target as HTMLInputElement | Dropdown;
                if (target?.name === "hero-category") {
                    filterParams = filterParams.replace(/&?filter\.p\.m\.product\.model=[^&]*/, "");
                }
                const responses = await Promise.all(this.getFetchPromises(filterParams));
                const data = responses[0];
                if (filterParams.includes("vendor") && this.selects["filter.p.m.product.model"]) {
                    this.currentModel = this.selects["filter.p.m.product.model"].value;
                    modelOptions = responses[1] as typeof modelOptions;
                }
                destroyPriceRangeFilter();
                this.replaceSelects(data);
                this.updateModelSelect(modelOptions);
                initPriceRangeFilter(this.selects["filter.v.price.gte"], this.selects["filter.v.price.lte"]);
                this.replaceButton(data);
                this.updateTabsBar();
            } catch (error) {
                console.error(error);
            } finally {
                this.loadOverlay.destroy();
            }
        }, 250);
    }

    /**
     * obtain fetch promises
     * @param params
     * @private
     */
    private getFetchPromises(params: string): (Promise<JSON> | Promise<HTMLOptionElement[]>)[] {
        const promises = [];

        const url = `${this.selectedCollectionURL}?sections=${Object.values(SECTIONS)
            .filter((s) => s.startsWith("hero"))
            .join(",")}${params}`;
        if (!this.buffer.cache[url]) {
            this.buffer.cache[url] = new Promise<JSON>((resolve) => {
                fetch(url)
                    .then((response) => response.json())
                    .then((data) => resolve(data));
            });
        }
        promises.push(this.buffer.cache[url]);

        if (this.selects["filter.p.m.product.model"] && params.includes("vendor")) {
            this.currentModel = this.selects["filter.p.m.product.model"]?.value;
            promises.push(
                new Promise<HTMLOptionElement[]>((resolve) => {
                    this.modelOptions
                        .fetch(
                            `${this.selectedCollectionURL}?sections=${SECTIONS.modelOptions}${params}`,
                            SECTIONS.modelOptions
                        )
                        .then((data) => resolve(data));
                })
            );
        }
        return promises;
    }

    /**
     * replace select elements
     * @param data
     */
    replaceSelects(data: any) {
        Object.values(SECTIONS).forEach((section) => {
            const doc = this.parser.parseFromString(data[section], "text/html");
            const selects = doc.querySelectorAll("x-dropdown") as NodeListOf<HTMLElement>;
            selects.forEach((select: Dropdown) => {
                const name = select.getAttribute("name");
                const current = this.selections.querySelector<Dropdown>(`x-dropdown[name="${name}"]`);
                const value = current.value;
                const selectedIndex = current.selectedIndex;
                this.selects[name] = select;
                current.removeEventListener("change", this.changeHandler);
                this.selections.replaceChild(select, current);
                if (
                    selectedIndex > 0 &&
                    value != "" &&
                    (select.options as HTMLOptionElement[]).find((o) => o.value === value)
                ) {
                    select.value = value;
                }
            });
        });
        Object.values(this.selects).forEach((select) => select.addEventListener("change", this.changeHandler));
    }

    /**
     * update model dropdown
     * @param options
     */
    updateModelSelect(options: HTMLOptionElement[]) {
        const select = this.selects["filter.p.m.product.model"];
        if (select && options.length > 0) {
            select.removeEventListener("change", this.changeHandler);
            options.forEach((o) => select.appendChild(o));
            if (this.currentModel) select.value = this.currentModel;
            this.currentModel = null;
            select.disabled = false;
            select.addEventListener("change", this.changeHandler);
        }
    }

    updateTabsBar() {
        const tabs = [...this.form.querySelectorAll<HTMLLabelElement>('label[for^="hero-category"]')].filter(
            (l) => l.htmlFor.indexOf("hero-category-all") < 0
        );
        const selectModel = this.selects["filter.p.m.product.model"];
        const selectVendor = this.selects["filter.p.vendor"] as Dropdown;
        const vendorMapOption = selectModel?.querySelector<HTMLOptionElement>("option[data-category-vendor-map]");
        const categoryVendorMapRaw = vendorMapOption?.dataset.categoryVendorMap;
        const currentVendors = [selectVendor.value];
        tabs.forEach((t) => (t.dataset.disabled = "false"));
        if (categoryVendorMapRaw && currentVendors.length) {
            const category_vendor_map = JSON.parse(categoryVendorMapRaw);
            const map: { [key: string]: string[] } = {};
            category_vendor_map.forEach((cv: string) => {
                const parts = cv.split(":");
                const vendor = parts[0];
                const cat = parts[1];
                map[vendor] = [...(map[vendor] || []), cat];
            });
            tabs.forEach((t) => {
                t.dataset.disabled = "true";
                currentVendors.forEach((v) => {
                    if (map[v]?.includes(document.getElementById(t.htmlFor).dataset.filter)) {
                        t.dataset.disabled = "false";
                    }
                });
            });
        }
    }

    /**
     * replace button
     * @param data
     */
    replaceButton(data: any) {
        const doc = this.parser.parseFromString(data[SECTIONS.button], "text/html");
        const button = doc.querySelector(".btn") as HTMLButtonElement;
        this.buttonBox.replaceChild(button, this.button);
    }

    set disabled(v: boolean) {
        this.button.disabled = !!v;
    }

    get button() {
        return this.buttonBox.querySelector<HTMLButtonElement>("button");
    }

    get filterParams() {
        let selections = Object.values(this.selects)
            .filter((select) => select.value)
            .map((select) => `${select.name}=${encodeURIComponent(select.value)}`)
            .join("&");
        if (selections.length > 0) {
            selections = `&${selections}`;
        }
        return selections;
    }

    get selectedCollectionURL() {
        const catUrl = this.categories
            .querySelector<HTMLElement>('input[type="radio"]:checked')
            //somhow, when there is a locale else than 'de' there is another '/' added, even if not visible
            .dataset.url.replace(new RegExp(`^/(${window.Shopify.locale}/)?`), "");
        return `${Shopify.routes.root}${catUrl}`;
    }
}

document.querySelector<HTMLElement>(".cms-hero-filter") && new HeroFilter();
